import React, { useLayoutEffect, useRef, useState } from "react";
import { Spinner } from "@/components/ui";
import { EmbedSnippet } from "../../components/EmbedSnippet";
import { MOBILE_PREVIEW_WIDTH, PreviewDevice } from "../../components/usePreviewPlayer";

const DESKTOP_PREVIEW_WIDTH = 1280;

interface Props {
  iframeRef: React.RefObject<HTMLIFrameElement>;
  iframeSrc: string;
  pageUrl: string;
  previewDevice: PreviewDevice;
  isLoading: boolean;
  previewPlayerName?: string;
  embedSnippet: string;
}

const useElementSize = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(([entry]) =>
      setSize({ width: entry.contentRect.width, height: entry.contentRect.height })
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return { ref, ...size };
};

const ReviewPagePreview: React.FC<Props> = ({
  iframeRef,
  iframeSrc,
  pageUrl,
  previewDevice,
  isLoading,
  previewPlayerName,
  embedSnippet,
}) => {
  const frame = useElementSize();
  const isMobilePreview = previewDevice === "mobile";
  const scale = !isMobilePreview && frame.width ? Math.min(1, frame.width / DESKTOP_PREVIEW_WIDTH) : 1;

  return (
    <div className="min-w-0 w-full overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
      <div className="flex items-center gap-3 border-b border-slate-200 bg-slate-100 px-3 py-2">
        <div className="flex gap-1.5" aria-hidden>
          <span className="size-2.5 rounded-full bg-slate-300" />
          <span className="size-2.5 rounded-full bg-slate-300" />
          <span className="size-2.5 rounded-full bg-slate-300" />
        </div>
        <div className="min-w-0 flex-1 truncate rounded-md bg-white px-3 py-1 text-xs text-slate-500">{pageUrl}</div>
      </div>

      <div ref={frame.ref} className="relative h-[calc(100vh-18rem)] min-h-[32rem] w-full overflow-hidden bg-white">
        <iframe
          key={iframeSrc}
          ref={iframeRef}
          title="Reviews Page preview"
          src={iframeSrc}
          className={`block border-0 bg-white ${isMobilePreview ? "mx-auto h-full" : "origin-top-left"}`}
          style={
            isMobilePreview
              ? { width: MOBILE_PREVIEW_WIDTH, maxWidth: "100%" }
              : { width: DESKTOP_PREVIEW_WIDTH, height: frame.height / scale || "100%", transform: `scale(${scale})` }
          }
        />
        {isLoading && (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 bg-white">
            <Spinner className="size-6 text-slate-500" />
            <p className="text-sm text-slate-500">Loading {previewPlayerName || "player"} preview…</p>
          </div>
        )}
      </div>

      <EmbedSnippet label="Embed on a website" snippet={embedSnippet} />
    </div>
  );
};

export { ReviewPagePreview };
